import { Button, MenuItem } from "@chakra-ui/react";
import AddCreditFacilityEventModal, {
  AllowedCreditFacilityFundFamilyEventSubTypes,
} from "features/credit-facility/CreditFacilityAddEventModal";
import { useCreditFacilityExpensesDelete } from "features/credit-facility/expenses/CreditFacilityExpensesDelete";
import {
  useCreditFacilityExpenseDetailsTableRetrieveSuspense,
  useCreditFacilityInterestExpenseEventsRetrieveSuspense,
} from "gen/endpoints/credit-facility/credit-facility";
import { FundFamilyEventSubTypeEnum } from "gen/model/fundFamilyEventSubTypeEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxActions } from "shared/components/table/box/TableBoxActions";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import {
  GetLinkInfoForRowFn,
  RenderRowActionsFn,
} from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const CreditFacilityExpenseTab: React.FC = () => {
  const { t } = useTranslation(["common", "creditFacility"]);
  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();
  const [eventSubType, setEventSubType] =
    useState<FundFamilyEventSubTypeEnum | null>(null);
  const { data: expenseEvents } =
    useCreditFacilityInterestExpenseEventsRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
    );

  const { data: detailedExpenses } =
    useCreditFacilityExpenseDetailsTableRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
    );

  const creditFacilityRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .creditFacilities({})
    .creditFacility({ creditFacilityId });

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) => {
      if (row.data?.metadata?.type !== "fund_family_event") {
        return undefined;
      }
      return {
        type: "generic",
        to: creditFacilityRoute
          .event({ fundFamilyEventId: row.data.metadata.id })
          .expenses({})
          .post({}).$,
      };
    },
    [creditFacilityRoute],
  );

  const confirmDeleteEvent = useCreditFacilityExpensesDelete(
    fundFamilyId,
    creditFacilityId,
  );

  const renderRowActions = useCallback<RenderRowActionsFn>(
    ({ row }) => {
      const metadata = row.data?.metadata;
      if (
        metadata?.type !== "fund_family_event" ||
        metadata.state !== "draft"
      ) {
        return undefined;
      }
      return {
        type: "menu",
        actions: (
          <MenuItem
            key="discard"
            className="destructive"
            onClick={async () => {
              await confirmDeleteEvent({ fundFamilyEventId: metadata.id });
            }}
          >
            {t("common:discardDraft")}
          </MenuItem>
        ),
      };
    },
    [confirmDeleteEvent, t],
  );

  return (
    <PageTabBody isFullHeight={true}>
      <TableBox isFullHeight={false}>
        <TableBoxHeader>
          <TableBoxTitle>{t("creditFacility:expenseEvent")}</TableBoxTitle>
          {expenseEvents.rows.length ? (
            <DataTableBoxActions>
              <Restricted permission={PermissionTypeEnum.write}>
                <Button
                  size="xs"
                  title={t("creditFacility:expenses")}
                  onClick={() =>
                    setEventSubType(FundFamilyEventSubTypeEnum.post_interest)
                  }
                >
                  {t("creditFacility:interestExpense.title")}
                </Button>
              </Restricted>
            </DataTableBoxActions>
          ) : (
            <TableBoxActions>
              <Restricted permission={PermissionTypeEnum.write}>
                <Button
                  size="xs"
                  title={t("creditFacility:expenses")}
                  onClick={() =>
                    setEventSubType(FundFamilyEventSubTypeEnum.post_interest)
                  }
                >
                  {t("creditFacility:interestExpense.title")}
                </Button>
              </Restricted>
            </TableBoxActions>
          )}
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={expenseEvents}
            getLinkInfoForRow={getLinkInfoForRow}
            renderRowActions={renderRowActions}
          />
        </TableBoxBody>
      </TableBox>
      <TableBox isFullHeight={false} isFullWidth={true}>
        <TableBoxHeader>
          <TableBoxTitle>
            {t("creditFacility:detailedInterestByUse")}
          </TableBoxTitle>
          <DataTableBoxActions showPivot={true} />
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable data={detailedExpenses} />
        </TableBoxBody>
      </TableBox>
      {eventSubType && (
        <AddCreditFacilityEventModal
          eventSubType={
            eventSubType as AllowedCreditFacilityFundFamilyEventSubTypes
          }
          onCloseComplete={() => setEventSubType(null)}
        />
      )}
    </PageTabBody>
  );
};
